// 016A — Test de Valoración de Edad Gestacional (Capurro + Ballard). Ver
// `edadGestacionalDatos.ts` para la transcripción completa de las 2 escalas
// y la fuente primaria (PDF oficial, página 70). El resultado (semanas)
// alimenta el Bloque D de 016 ("edad gestacional por... Capurro/Ballard")
// y el eje X de 016B/016C (Fenton) — cuando se construyan, deben leer el
// `Observation` LOINC 49051-6 más reciente de este formulario en vez de
// volver a pedir el dato.
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { EscalaClinica } from './uci/EscalaClinica';
import {
  DEFINICION_BALLARD_NEUROMUSCULAR,
  DEFINICION_CAPURRO,
  definicionBallardFisica,
  edadGestacionalBallardEnSemanas,
  edadGestacionalCapurroEnSemanas,
  estadoInicialEdadGestacional,
  indiceMadurezBallard,
  observacionesEdadGestacional,
  paresDeEdadGestacional,
  type SexoBiologico,
} from './edadGestacionalDatos';
import { calcularTotalEscala } from './uci/EscalaClinica';

function sexoBiologicoDe(gender: Patient['gender'] | undefined): SexoBiologico | undefined {
  return gender === 'male' || gender === 'female' ? gender : undefined;
}

export function Formulario016A({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const patient = useResource<Patient>(subject);
  const sexo = sexoBiologicoDe(patient?.gender);
  const [estado, setEstado] = useState(() => estadoInicialEdadGestacional());
  const { guardando, error, guardar } = useGuardado();
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '016A', t('hub.formulario.016A'), {
    // Test de edad gestacional (Capurro/Ballard) del recién nacido: servicio de Neonatología, no consulta externa.
    servicio: 'Neonatología',
  });

  const totalCapurro = calcularTotalEscala(DEFINICION_CAPURRO, estado.capurro);
  const indiceBallard = indiceMadurezBallard(sexo, estado);

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();
      const observaciones = observacionesEdadGestacional(subject, encounter, sexo, estado, ahora);
      observaciones.forEach((o) => lote.crear(o));
      const pares: Par[] = paresDeEdadGestacional(sexo, estado);
      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f016a.capurroTitulo')}</h4>
        <EscalaClinica definicion={DEFINICION_CAPURRO} respuestas={estado.capurro} onChange={(v) => setEstado({ ...estado, capurro: v })} />
        {totalCapurro !== undefined && (
          <p className="text-sm font-medium text-hce-ink">{t('formularios.f016a.edadGestacionalCapurro', { semanas: edadGestacionalCapurroEnSemanas(totalCapurro).toFixed(1) })}</p>
        )}
      </div>

      <div className="my-1 border-t border-border" />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f016a.ballardNeuromuscularTitulo')}</h4>
        <EscalaClinica
          definicion={DEFINICION_BALLARD_NEUROMUSCULAR}
          respuestas={estado.ballardNeuromuscular}
          onChange={(v) => setEstado({ ...estado, ballardNeuromuscular: v })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f016a.ballardFisicaTitulo')}</h4>
        {sexo ? (
          <EscalaClinica definicion={definicionBallardFisica(sexo)} respuestas={estado.ballardFisica} onChange={(v) => setEstado({ ...estado, ballardFisica: v })} />
        ) : (
          <p className="text-sm text-hce-muted">{t('formularios.f016a.sexoNoRegistrado')}</p>
        )}
        {indiceBallard !== undefined && (
          <p className="text-sm font-medium text-hce-ink">
            {t('formularios.f016a.edadGestacionalBallard', { semanas: edadGestacionalBallardEnSemanas(indiceBallard).toFixed(1) })}
          </p>
        )}
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
